/**
 * The receive store (FEAT-040 wave 3) — the offline inbox's bytes and truth.
 *
 * A second database with the first one's scars. `momento-media` holds what
 * this phone is SENDING; this holds what it has RECEIVED and not yet viewed.
 * It is deliberately its own database: new object stores in momento-media
 * would force the version bump the send campaign engineered to never need
 * (a versionchange while another connection is open is one of Safari IDB's
 * classic ways to hang an app), and received rows would pollute the drafts
 * sheet's metadata walks. The idioms are copied, not abstracted — the send
 * store is certified and stays untouched.
 *
 * - `inbox-meta`   — one SMALL record per message: the full MessageResponse
 *   envelope (~1-2 KB) plus download state. No bytes field on the type
 *   (`bytes?: never`), so no metadata read can ever return a video.
 * - `inbox-chunks` — the bytes, as ~2 MiB rows keyed `[messageId, seq]`.
 *
 * Commit semantics INVERT the send side's meta-last rule: the meta row is
 * born at ENQUEUE (`state: 'pending'`, envelope only) because it IS the
 * durable unread mirror and carries retry state. Chunks stream in, and the
 * single meta put that flips it to `'stored'` is the commit. A kill
 * mid-download leaves a pending row plus orphan chunks — the sweep clears
 * the chunks, the engine downloads again. `assembleBlob` returns null unless
 * stored + chunks agree; a torn write reads as "not stored yet", never as
 * damaged playback.
 *
 * Shred order is META FIRST, chunks second (the same rule as
 * `mediaStore.remove`): a crash between the deletes leaves invisible orphan
 * chunks for the sweep — never a stored-claiming head whose body is gone,
 * which could re-enter play queues and get a view-once RE-DOWNLOADED after
 * viewing.
 *
 * Failure policy: FAILS LOUD (rejects), same as the send store.
 */
import type { Message } from '../../types';

export const INBOX_DB_NAME = 'momento-inbox';
export const INBOX_DB_VERSION = 1;
export const INBOX_META_STORE = 'inbox-meta';
export const INBOX_CHUNK_STORE = 'inbox-chunks';
export const INBOX_KV_STORE = 'kv';

/** Same row size as the send store's chunks — a proven number for IDB puts —
 * but no PATCH-body identity here; nothing uploads these. */
export const INBOX_CHUNK_BYTES = 2 * 1024 * 1024;

/** Stored (bytes-holding) items per user. Unviewed moments are a short-lived
 * population — 24h/7d retention or the next reconcile clears them — so forty
 * is generous headroom, not a promise. */
export const MAX_STORED_ITEMS = 40;

/** Meta rows in total (pending included). Past this, enqueue refuses and the
 * moment stays online-only — a bound on every mirror walk, not a budget. */
export const MAX_META_ROWS = 200;

/** Byte budget fallback when `storage.estimate()` is unavailable. */
export const MAX_TOTAL_BYTES = 500 * 1024 * 1024;

/** Never more than this. The send store takes half the origin quota; a
 * quarter here keeps the two of them under three quarters combined. */
const BUDGET_CEILING_BYTES = 2 * 1024 * 1024 * 1024;

/** Thrown when a download could NEVER fit — the engine treats it as
 * permanent (drop the row, fall back to the online path). */
export class ReceiveBudgetError extends Error {}

/** Thrown when the row vanished mid-download (shredded by a view or a
 * reconcile) — the commit must not resurrect it. Permanent for the engine. */
export class ReceiveRowGoneError extends Error {}

export interface InboxMeta {
  /** DM message id — the same id space the viewer and prefetcher speak. */
  messageId: number;
  /** The recipient (this account) — per-account isolation on shared devices. */
  userId: number;
  /** The sender — the offline play queue groups by this. */
  friendId: number;
  /** 'pending' = enqueued, no bytes yet (the durable unread mirror).
   * 'stored' = bytes committed and playable offline. */
  state: 'pending' | 'stored';
  attempts: number;
  nextAttemptAt?: number;
  /** The FULL MessageResponse, verbatim — offline it must stand in for a
   * fetchUnreadSnaps row. Small (~1-2 KB), safe in a whole-store walk. */
  envelope: Message;
  /** Set by the commit flip. */
  size?: number;
  chunkCount?: number;
  mimeType?: string;
  createdAt: number;
  updatedAt: number;
  /** The disease, banned at the type level (see mediaStore's header). */
  bytes?: never;
}

interface ChunkRow {
  messageId: number;
  seq: number;
  bytes: ArrayBuffer;
}

type ChangeCallback = () => void;

class ReceiveStoreService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private closed = false;
  private changeCallbacks = new Set<ChangeCallback>();
  private budgetPromise: Promise<number> | null = null;
  private watchingForeground = false;

  /**
   * The in-memory mirror of `inbox-meta` — written only AFTER the disk write
   * lands (the ledger's discipline), EXCEPT shred, which removes eagerly so a
   * consumed view-once is gone from every synchronous reader before the disk
   * catches up. It is what makes `hasStored()` / `envelopesFor()` synchronous;
   * it is never the source of truth.
   */
  private mirror = new Map<number, InboxMeta>();

  init(): Promise<void> {
    if (this.closed) return Promise.resolve();
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.open()
      .then(() => this.loadMirror())
      .catch((err) => {
        this.initPromise = null;
        throw err;
      });
    return this.initPromise;
  }

  /** The readers' "may I trust the mirror yet?" signal (BUG-122's lesson —
   * before this resolves, synchronous answers come from an EMPTY mirror). */
  whenReady(): Promise<void> {
    return this.init();
  }

  private open(attempt = 0): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('receive store: IndexedDB open timed out'));
      }, 5000);

      const request = indexedDB.open(INBOX_DB_NAME, INBOX_DB_VERSION);

      request.onupgradeneeded = () => {
        // Store creation ONLY — no data work in a versionchange transaction.
        const db = request.result;
        if (!db.objectStoreNames.contains(INBOX_META_STORE)) {
          db.createObjectStore(INBOX_META_STORE, { keyPath: 'messageId' });
        }
        if (!db.objectStoreNames.contains(INBOX_CHUNK_STORE)) {
          db.createObjectStore(INBOX_CHUNK_STORE, { keyPath: ['messageId', 'seq'] });
        }
        if (!db.objectStoreNames.contains(INBOX_KV_STORE)) {
          // Empty on purpose — created at v1 so a later wave never needs the
          // version bump this database exists to avoid.
          db.createObjectStore(INBOX_KV_STORE, { keyPath: 'key' });
        }
      };

      request.onsuccess = () => {
        clearTimeout(timeout);
        if (this.closed) {
          request.result.close();
          resolve();
          return;
        }
        this.db = request.result;
        this.db.onversionchange = () => {
          this.db?.close();
          this.db = null;
          this.initPromise = null;

        };
        resolve();
      };

      request.onerror = () => {
        clearTimeout(timeout);
        // Safari's known first-open flake: one retry before failing loud.
        if (attempt === 0) {
          this.open(1).then(resolve, reject);
        } else {
          reject(request.error ?? new Error('receive store: open failed'));
        }
      };
      request.onblocked = () => {
        clearTimeout(timeout);
        reject(new Error('receive store: blocked by another connection'));
      };
    });
  }

  private async loadMirror(): Promise<void> {
    if (!this.db) return;
    // Raw transaction, NOT tx(): this runs inside init()'s own promise chain,
    // and tx() awaits init() — routing through it is a deadlock on ourselves.
    const txn = this.db.transaction(INBOX_META_STORE, 'readonly');
    const rows = await this.req(txn.objectStore(INBOX_META_STORE).getAll());
    this.mirror = new Map(
      (rows as InboxMeta[]).map((row) => [row.messageId, row])
    );

    // The mirror just went from empty to real — every subscriber that
    // computed against the pre-boot emptiness is wrong RIGHT NOW (BUG-122).
    this.fireChange();
  }

  private async tx<T>(
    stores: string | string[],
    mode: IDBTransactionMode,
    run: (tx: IDBTransaction) => Promise<T> | T
  ): Promise<T> {
    await this.init();
    if (!this.db) throw new Error('receive store: closed');
    const tx = this.db.transaction(stores, mode);
    return run(tx);
  }

  private req<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ---- enqueue (the durable unread mirror) ----

  /**
   * A moment arrived — remember it durably before any bytes move. Idempotent:
   * an id already known (pending OR stored) is left alone. Refuses past the
   * meta-row ceiling — that moment simply stays online-only.
   */
  async enqueue(userId: number, envelope: Message): Promise<boolean> {
    await this.init();
    if (this.mirror.has(envelope.id)) return false;
    if (this.mirror.size >= MAX_META_ROWS) return false;

    const row: InboxMeta = {
      messageId: envelope.id,
      userId,
      friendId: envelope.sender_id,
      state: 'pending',
      attempts: 0,
      envelope,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    await this.tx(INBOX_META_STORE, 'readwrite', (tx) =>
      this.req(tx.objectStore(INBOX_META_STORE).put(row))
    );
    this.mirror.set(row.messageId, row);
    this.fireChange();
    return true;
  }

  // ---- synchronous mirror reads ----

  /** The engine's work list: pending rows, oldest first. */
  pending(): InboxMeta[] {
    return [...this.mirror.values()]
      .filter((row) => row.state === 'pending')
      .sort((a, b) => a.createdAt - b.createdAt);
  }

  /** Known at all (pending or stored) — the prefetcher skip-list. */
  has(messageId: number): boolean {
    return this.mirror.has(messageId);
  }

  /** Bytes committed and playable — the viewer's probe. */
  hasStored(messageId: number): boolean {
    return this.mirror.get(messageId)?.state === 'stored';
  }

  get(messageId: number): InboxMeta | undefined {
    return this.mirror.get(messageId);
  }

  /** Every meta row, for the reconcile sweep. */
  all(): InboxMeta[] {
    return [...this.mirror.values()];
  }

  /**
   * The offline run for one conversation: STORED only (a pending row has no
   * bytes to show), oldest first — the same order the server's unread list
   * plays in.
   */
  envelopesFor(userId: number, friendId: number): Message[] {
    return [...this.mirror.values()]
      .filter(
        (row) =>
          row.userId === userId &&
          row.friendId === friendId &&
          row.state === 'stored'
      )
      .sort((a, b) => a.createdAt - b.createdAt)
      .map((row) => row.envelope);
  }

  totalStoredBytes(userId: number): number {
    return [...this.mirror.values()]
      .filter((row) => row.userId === userId && row.state === 'stored')
      .reduce((sum, row) => sum + (row.size ?? 0), 0);
  }

  // ---- the download's write path ----

  /** A quarter of the origin's quota (the send store takes half), capped. */
  private byteBudget(): Promise<number> {
    if (!this.budgetPromise) {
      this.watchForeground();
      this.budgetPromise = (async () => {
        try {
          const quota = (await navigator.storage?.estimate?.())?.quota;
          if (!quota) return MAX_TOTAL_BYTES;
          return Math.min(BUDGET_CEILING_BYTES, Math.floor(quota / 4));
        } catch {
          return MAX_TOTAL_BYTES;
        }
      })();
    }
    return this.budgetPromise;
  }

  private watchForeground(): void {
    if (this.watchingForeground || typeof document === 'undefined') return;
    this.watchingForeground = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.budgetPromise = null;
    });
  }

  /**
   * Stream a download's body into chunk rows, then commit with the state
   * flip. Constant memory: a 2 MiB accumulator of COPIED buffers (the reader
   * may reuse its allocation), one put in flight at a time.
   *
   * Before the flip, the budget is enforced by evicting the OLDEST stored
   * rows (never pending ones — they hold no bytes and are the unread mirror)
   * until the newcomer fits. A body that could never fit throws
   * `ReceiveBudgetError`; a row shredded mid-download throws
   * `ReceiveRowGoneError` and leaves nothing behind — both permanent to the
   * engine.
   */
  async saveStream(
    messageId: number,
    body: ReadableStream<Uint8Array>,
    mimeType: string,
    expectedSize?: number
  ): Promise<InboxMeta> {
    await this.init();
    const row = this.mirror.get(messageId);
    if (!row) throw new ReceiveRowGoneError(`receive store: no row ${messageId}`);

    const parts: Uint8Array[] = [];
    let buffered = 0;
    let seq = 0;
    let written = 0;

    const putChunk = async (bytes: ArrayBuffer): Promise<void> => {
      const chunk: ChunkRow = { messageId, seq: seq++, bytes };
      written += bytes.byteLength;
      await this.tx(INBOX_CHUNK_STORE, 'readwrite', (tx) =>
        this.req(tx.objectStore(INBOX_CHUNK_STORE).put(chunk))
      );
    };

    const takeChunk = (): ArrayBuffer => {
      const out = new Uint8Array(INBOX_CHUNK_BYTES);
      let offset = 0;
      while (offset < INBOX_CHUNK_BYTES) {
        const head = parts[0];
        const need = INBOX_CHUNK_BYTES - offset;
        if (head.byteLength <= need) {
          out.set(head, offset);
          offset += head.byteLength;
          parts.shift();
        } else {
          out.set(head.subarray(0, need), offset);
          parts[0] = head.subarray(need);
          offset += need;
        }
      }
      buffered -= INBOX_CHUNK_BYTES;
      return out.buffer;
    };

    const reader = body.getReader();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value || value.byteLength === 0) continue;
        // Copy: the stream may reuse its buffer between reads.
        parts.push(new Uint8Array(value));
        buffered += value.byteLength;
        while (buffered >= INBOX_CHUNK_BYTES) {
          await putChunk(takeChunk());
        }
      }
      if (buffered > 0) {
        const tail = new Uint8Array(buffered);
        let offset = 0;
        for (const part of parts) {
          tail.set(part, offset);
          offset += part.byteLength;
        }
        parts.length = 0;
        buffered = 0;
        await putChunk(tail.buffer);
      }

      // A body shorter than the server declared is a connection that died
      // politely, not a moment — it must not commit (the engine retries).
      if (expectedSize !== undefined && written !== expectedSize) {
        throw new Error(
          `receive store: short body (${written} of ${expectedSize})`
        );
      }

      const budget = await this.byteBudget();
      if (written > budget) {
        throw new ReceiveBudgetError('receive store: body exceeds byte budget');
      }
      await this.evictToFit(row.userId, written);

      // The commit: ONE meta put flipping pending → stored. Re-read the live
      // row first — a shred that landed mid-download must win, and this put
      // must not resurrect it.
      const current = this.mirror.get(messageId);
      if (!current || current.state !== 'pending') {
        throw new ReceiveRowGoneError(
          `receive store: row ${messageId} gone or changed before commit`
        );
      }
      const committed: InboxMeta = {
        ...current,
        state: 'stored',
        size: written,
        chunkCount: seq,
        mimeType,
        updatedAt: Date.now(),
      };
      await this.tx(INBOX_META_STORE, 'readwrite', (tx) =>
        this.req(tx.objectStore(INBOX_META_STORE).put(committed))
      );
      this.mirror.set(messageId, committed);
      this.fireChange();
      return committed;
    } catch (err) {
      // Leave no headless bytes behind a failed or refused download.
      await this.deleteChunks(messageId).catch(() => {});
      throw err;
    } finally {
      reader.releaseLock();
    }
  }

  /** Evict the oldest STORED rows (this user's) until `incoming` fits. */
  private async evictToFit(userId: number, incoming: number): Promise<void> {
    const budget = await this.byteBudget();
    const stored = () =>
      [...this.mirror.values()]
        .filter((row) => row.userId === userId && row.state === 'stored')
        .sort((a, b) => a.createdAt - b.createdAt);

    let mine = stored();
    let bytes = mine.reduce((sum, row) => sum + (row.size ?? 0), 0);
    while (mine.length >= MAX_STORED_ITEMS || bytes + incoming > budget) {
      const oldest = mine[0];
      if (!oldest) break; // nothing left to evict — the budget check above rules
      await this.shred(oldest.messageId);
      mine = stored();
      bytes = mine.reduce((sum, row) => sum + (row.size ?? 0), 0);
    }
  }

  // ---- reading ----

  /**
   * The whole media as one Blob — ONE item, on a tap. Answers from the
   * MIRROR's state first, so a shredded row can never assemble even if its
   * disk deletes are still in flight. Null = not stored / torn — the caller
   * falls back to the network path.
   */
  async assembleBlob(messageId: number): Promise<Blob | null> {
    await this.init();
    const row = this.mirror.get(messageId);
    if (!row || row.state !== 'stored' || row.chunkCount === undefined) {
      return null;
    }
    const pieces: Blob[] = [];
    let total = 0;
    for (let seq = 0; seq < row.chunkCount; seq++) {
      const chunk = (await this.tx(INBOX_CHUNK_STORE, 'readonly', (tx) =>
        this.req(tx.objectStore(INBOX_CHUNK_STORE).get([messageId, seq]))
      )) as ChunkRow | undefined;
      if (!chunk) return null; // torn: a committed row must have every chunk
      total += chunk.bytes.byteLength;
      pieces.push(new Blob([chunk.bytes]));
    }
    if (total !== row.size) return null;
    return new Blob(pieces, { type: row.mimeType });
  }

  // ---- retry bookkeeping (the engine's) ----

  async patch(
    messageId: number,
    updates: Partial<Pick<InboxMeta, 'attempts' | 'nextAttemptAt'>>
  ): Promise<void> {
    await this.init();
    const row = this.mirror.get(messageId);
    if (!row) return;
    const next: InboxMeta = { ...row, ...updates, updatedAt: Date.now() };
    await this.tx(INBOX_META_STORE, 'readwrite', (tx) =>
      this.req(tx.objectStore(INBOX_META_STORE).put(next))
    );
    this.mirror.set(messageId, next);
  }

  // ---- shred (the privacy heart) ----

  /**
   * META FIRST — eagerly out of the mirror, then the disk row, then the
   * chunks. A crash mid-way leaves invisible orphan chunks for the sweep,
   * never a head that could re-enter a play queue. Idempotent.
   */
  async shred(messageId: number): Promise<void> {
    // Mirror removal happens THIS TICK, before any await — a consumed
    // view-once must be gone from hasStored()/envelopesFor() the moment the
    // caller says so, not a microtask later when a probe may already have
    // raced in.
    const existed = this.mirror.delete(messageId);
    if (existed) this.fireChange();
    await this.init();
    // A load that was in flight when we deleted may have re-listed the row —
    // the second delete closes that window (idempotent otherwise).
    if (this.mirror.delete(messageId)) this.fireChange();
    await this.tx(INBOX_META_STORE, 'readwrite', (tx) =>
      this.req(tx.objectStore(INBOX_META_STORE).delete(messageId))
    );
    await this.deleteChunks(messageId);
  }

  /** Account switch on a shared device: the previous account's inbox goes. */
  async shredOtherUsers(userId: number): Promise<void> {
    const others = [...this.mirror.values()].filter((row) => row.userId !== userId);
    for (const row of others) await this.shred(row.messageId);
  }

  /** Sign-out: nothing received survives. */
  async clearAll(): Promise<void> {
    const ids = [...this.mirror.keys()];
    for (const id of ids) await this.shred(id);
  }

  private deleteChunks(messageId: number): Promise<void> {
    return this.tx(INBOX_CHUNK_STORE, 'readwrite', (tx) =>
      this.req(
        tx.objectStore(INBOX_CHUNK_STORE).delete(
          IDBKeyRange.bound([messageId, 0], [messageId, Number.MAX_SAFE_INTEGER])
        )
      ).then(() => undefined)
    );
  }

  /**
   * Delete chunk runs with no meta row — a kill mid-download or mid-shred.
   * Keys-only cursor: a value cursor over the chunk store would deserialise
   * the bytes it exists to protect.
   */
  async sweepOrphanChunks(): Promise<number> {
    await this.init();
    const metaIds = new Set(this.mirror.keys());
    const orphanIds = await this.tx(INBOX_CHUNK_STORE, 'readonly', (tx) => {
      return new Promise<Set<number>>((resolve, reject) => {
        const found = new Set<number>();
        const request = tx.objectStore(INBOX_CHUNK_STORE).openKeyCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) {
            resolve(found);
            return;
          }
          const [messageId] = cursor.primaryKey as [number, number];
          if (!metaIds.has(messageId)) found.add(messageId);
          cursor.continue();
        };
        request.onerror = () => reject(request.error);
      });
    });
    for (const id of orphanIds) await this.deleteChunks(id);
    return orphanIds.size;
  }

  // ---- change signal ----

  /** Fires immediately on subscribe (surfaces render current state first). */
  onChange(cb: ChangeCallback): () => void {
    this.changeCallbacks.add(cb);
    cb();
    return () => this.changeCallbacks.delete(cb);
  }

  private fireChange(): void {
    this.changeCallbacks.forEach((cb) => cb());
  }

  // ---- lifecycle (tests) ----

  close(): void {
    this.closed = true;
    this.initPromise = null;
    this.db?.close();
    this.db = null;
    this.mirror.clear();

  }

  _reopen(): void {
    this.closed = false;
    this.budgetPromise = null;
  }
}

export const receiveStore = new ReceiveStoreService();
